/**
 * 软件主题（Software Theme）服务 —— 主入口。
 *
 * 职责：维护用户选择的主题状态（system / preset / custom）、调度 CSS 变量写入、
 * 写入 DOM 上的标记属性、向 localStorage 持久化。
 *
 * 拆分约定：
 * - createThemeTokens 等 200+ 行的 token 计算 → src/service/software_theme_tokens.ts
 * - 与 brand 相关的桥接（daylight 浅色由 brand 派生、brand 接管的 token 还原） → src/service/software_theme_brand_bridge.ts
 *
 * 调用方一律继续从 `@/service/software_theme` 引入，所有 public API 名称稳定。
 */

import { computed, reactive, ref } from 'vue'
import { STORAGE_KEYS } from '@libs/define/storage_key'
import { clamp, mixHexColor as mixColor, normalizeHexColor, withAlpha as withOpacity } from '@/utils/color'
import { createThemeTokens, isLightStageTokens, type ThemeCssTokens } from '@/service/software_theme_tokens'
import { brandManagedThemeTokenKeys, getActiveBrandTokens, presetKeys, resolvePresetSeeds, resolveSoftwareThemeOptions, resolveSystemPreviewPalette, type SoftwareThemePreset } from '@/service/software_theme_brand_bridge'
import { closeOtherAppearancePanels, notifyAppearancePanelClosed, notifyAppearancePanelOpened, registerAppearancePanelCloser } from '@/service/appearance_panel_registry'

const APPEARANCE_PANEL_ID = 'software_theme'

export type { SoftwareThemePreset }
export type SoftwareThemeMode = 'system' | 'preset' | 'custom'
export type SoftwareThemeColorField = 'stage' | 'accent'
export type SoftwareThemeSliderField = 'depth'

export type SoftwareThemeCustomColors = {
  stage: string
  accent: string
}

export type SoftwareThemeCustomSettings = {
  depth: number
}

type SoftwareThemeState = {
  mode: SoftwareThemeMode
  preset: SoftwareThemePreset | null
  customColors: SoftwareThemeCustomColors
  customSettings: SoftwareThemeCustomSettings
}

type SoftwareThemeStorage = SoftwareThemeState & {
  version: 2
}

type StudioPalette = {
  accent: string
  accentSoft: string
  accentBorder: string
  surfaceSoft: string
  surfaceStrong: string
  shadowSoft: string
  shadowStrong: string
  buttonBg: string
  buttonShadow: string
  buttonShadowHover: string
}

const SOFTWARE_THEME_SCHEMA_VERSION = 2 as const

const DEFAULT_CUSTOM_COLORS: SoftwareThemeCustomColors = {
  stage: '#6ee7b7',
  accent: '#8b5cf6',
}

const DEFAULT_CUSTOM_SETTINGS: SoftwareThemeCustomSettings = {
  depth: 54,
}

const DEFAULT_THEME: SoftwareThemeState = {
  mode: 'system',
  preset: null,
  customColors: { ...DEFAULT_CUSTOM_COLORS },
  customSettings: { ...DEFAULT_CUSTOM_SETTINGS },
}

const modeKeys = ['system', 'preset', 'custom'] as const

export const softwareThemeColorFields: Array<{ key: SoftwareThemeColorField; labelKey: string }> = [
  { key: 'stage', labelKey: 'software_theme_custom_primary' },
  { key: 'accent', labelKey: 'software_theme_custom_accent' },
]

export const softwareThemeSliderFields: Array<{ key: SoftwareThemeSliderField; labelKey: string }> = [{ key: 'depth', labelKey: 'software_theme_custom_intensity' }]

// ────────────────────────────────────────────────────────────────────────────────
// region: 模块状态
// ────────────────────────────────────────────────────────────────────────────────

const themeState = reactive<SoftwareThemeState>({
  ...DEFAULT_THEME,
  customColors: { ...DEFAULT_CUSTOM_COLORS },
  customSettings: { ...DEFAULT_CUSTOM_SETTINGS },
})

export const softwareThemePanelVisible = ref(false)

const softwareThemeBrandRefreshKey = ref(0)

let initialized = false
let pendingApplyFrame = 0
let pendingApplyTimer: number | null = null
let pendingPersistTimer: number | null = null
let appliedThemeTokens: ThemeCssTokens | null = null

const CUSTOM_THEME_APPLY_DEBOUNCE = 96

// ────────────────────────────────────────────────────────────────────────────────
// region: 输入归一化
// ────────────────────────────────────────────────────────────────────────────────

const normalizeSliderValue = (value: unknown, fallback: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback
  }

  return Math.round(clamp(value, 0, 100))
}

const normalizeCustomColors = (value?: Partial<SoftwareThemeCustomColors> | null): SoftwareThemeCustomColors => {
  return {
    stage: normalizeHexColor(value?.stage, DEFAULT_CUSTOM_COLORS.stage),
    accent: normalizeHexColor(value?.accent, DEFAULT_CUSTOM_COLORS.accent),
  }
}

const normalizePartialCustomColors = (value?: Partial<SoftwareThemeCustomColors> | null): Partial<SoftwareThemeCustomColors> => {
  if (!value) {
    return {}
  }

  const nextColors: Partial<SoftwareThemeCustomColors> = {}

  if (Object.prototype.hasOwnProperty.call(value, 'stage')) {
    nextColors.stage = normalizeHexColor(value.stage, DEFAULT_CUSTOM_COLORS.stage)
  }

  if (Object.prototype.hasOwnProperty.call(value, 'accent')) {
    nextColors.accent = normalizeHexColor(value.accent, DEFAULT_CUSTOM_COLORS.accent)
  }

  return nextColors
}

const normalizeCustomSettings = (value?: Partial<SoftwareThemeCustomSettings> | null): SoftwareThemeCustomSettings => {
  return {
    depth: normalizeSliderValue(value?.depth, DEFAULT_CUSTOM_SETTINGS.depth),
  }
}

const normalizePartialCustomSettings = (value?: Partial<SoftwareThemeCustomSettings> | null): Partial<SoftwareThemeCustomSettings> => {
  if (!value) {
    return {}
  }

  const nextSettings: Partial<SoftwareThemeCustomSettings> = {}

  if (Object.prototype.hasOwnProperty.call(value, 'depth')) {
    nextSettings.depth = normalizeSliderValue(value.depth, DEFAULT_CUSTOM_SETTINGS.depth)
  }

  return nextSettings
}

const isThemePreset = (value: unknown): value is SoftwareThemePreset => {
  return typeof value === 'string' && (presetKeys as readonly string[]).includes(value)
}

const isThemeMode = (value: unknown): value is SoftwareThemeMode => {
  return typeof value === 'string' && (modeKeys as readonly string[]).includes(value)
}

const normalizeSoftwareTheme = (value: unknown): SoftwareThemeState | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  // 版本兼容策略：未知版本号也按当前结构尝试 normalize 兜底，避免下次 schema 升级时
  // 静默丢失用户的自定义颜色。与 keyboard_appearance 的策略对齐。
  const payload = value as Partial<SoftwareThemeStorage> & { version?: unknown }

  const mode = isThemeMode(payload.mode) ? payload.mode : DEFAULT_THEME.mode
  const preset = isThemePreset(payload.preset) ? payload.preset : null

  return {
    mode: mode === 'preset' && !preset ? 'system' : mode,
    preset,
    customColors: normalizeCustomColors(payload.customColors),
    customSettings: normalizeCustomSettings(payload.customSettings),
  }
}

// ────────────────────────────────────────────────────────────────────────────────
// region: token 解析（state → tokens 与衍生预览色板）
// ────────────────────────────────────────────────────────────────────────────────

const resolveThemeTokens = (state: SoftwareThemeState): ThemeCssTokens | null => {
  if (state.mode === 'custom') {
    return createThemeTokens({
      stage: state.customColors.stage,
      accent: state.customColors.accent,
      depth: state.customSettings.depth,
    })
  }

  if (state.mode === 'preset' && state.preset) {
    return createThemeTokens(resolvePresetSeeds()[state.preset])
  }

  return null
}

const resolveStudioPalette = (tokens: ThemeCssTokens | null): StudioPalette => {
  return {
    accent: tokens?.['--brand-color-pure'] ?? 'var(--brand-color-pure)',
    accentSoft: tokens?.['--action-btn-primary-soft-bg'] ?? 'var(--action-btn-primary-soft-bg)',
    accentBorder: tokens?.['--action-btn-primary-soft-border'] ?? 'var(--action-btn-primary-soft-border)',
    surfaceSoft: tokens?.['--settings-card-bg'] ?? 'var(--settings-card-bg)',
    surfaceStrong: tokens?.['--glass-panel-bg'] ?? 'var(--glass-panel-bg)',
    shadowSoft: tokens?.['--action-btn-primary-shadow'] ?? 'var(--action-btn-primary-shadow)',
    shadowStrong: tokens?.['--action-btn-primary-shadow-hover'] ?? 'var(--action-btn-primary-shadow-hover)',
    buttonBg: tokens?.['--action-btn-primary-bg'] ?? 'var(--action-btn-primary-bg)',
    buttonShadow: tokens?.['--action-btn-primary-shadow'] ?? 'var(--action-btn-primary-shadow)',
    buttonShadowHover: tokens?.['--action-btn-primary-shadow-hover'] ?? 'var(--action-btn-primary-shadow-hover)',
  }
}

const resolveStudioStagePalette = (tokens: ThemeCssTokens | null) => {
  const brandTokens = getActiveBrandTokens()
  // stage = 舞台主色（写入 --color-brand-1）；accent = 强调色（写入 --brand-color-pure）。
  // 历史 bug：两个变量都从 --color-brand-1 读，导致 accent 与 stage 永远相等。
  const stage = normalizeHexColor(tokens?.['--color-brand-1'] ?? brandTokens['--color-brand-1'], '#ff8f60')
  const accent = normalizeHexColor(tokens?.['--brand-color-pure'] ?? brandTokens['--brand-color-pure'], '#ff8f60')
  // 与 createThemeTokens 完全一致：从已渲染 token 中读取 isLightStage 标志。
  const isLightStage = isLightStageTokens(tokens)
  const glowA = mixColor(stage, '#ffffff', 0.28)
  const glowB = mixColor(accent, '#ffffff', 0.2)
  const deepBase = mixColor(accent, '#0a1325', 0.14)

  return {
    background: isLightStage
      ? `radial-gradient(circle at 16% 18%, ${withOpacity(glowA, 0.3)} 0%, ${withOpacity(glowA, 0)} 36%), radial-gradient(circle at 86% 24%, ${withOpacity(glowB, 0.22)} 0%, ${withOpacity(glowB, 0)} 34%), radial-gradient(circle at 24% 86%, ${withOpacity(mixColor('#fff7ee', stage, 0.34), 0.66)} 0%, ${withOpacity(mixColor('#fff7ee', stage, 0.34), 0)} 42%), linear-gradient(150deg, ${mixColor('#f8fbff', stage, 0.18)} 0%, ${mixColor('#edf5ff', accent, 0.18)} 52%, ${mixColor('#ffffff', accent, 0.08)} 100%)`
      : `radial-gradient(circle at 16% 18%, ${withOpacity(glowA, 0.24)} 0%, ${withOpacity(glowA, 0)} 34%), radial-gradient(circle at 84% 26%, ${withOpacity(glowB, 0.18)} 0%, ${withOpacity(glowB, 0)} 32%), linear-gradient(145deg, ${mixColor(stage, '#ffffff', 0.24)} 0%, ${mixColor(stage, accent, 0.32)} 46%, ${deepBase} 100%)`,
  }
}

const resolveSoftwareThemeTone = (tokens: ThemeCssTokens | null): 'light' | 'dark' | 'brand' => {
  if (!tokens) {
    return 'brand'
  }

  return isLightStageTokens(tokens) ? 'light' : 'dark'
}

// ────────────────────────────────────────────────────────────────────────────────
// region: DOM 写入（标记属性 + token patch + brand 还原）
// ────────────────────────────────────────────────────────────────────────────────

const canUseStorage = (): boolean => {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

const getThemeTargets = () => {
  if (typeof document === 'undefined') {
    return []
  }

  return [document.documentElement, document.body]
}

const applySoftwareThemePanelStateAttribute = (isOpen: boolean) => {
  // 兼容保留：仍然写一份 `software-theme-panel-open` 供可能依赖它的选择器使用。
  // 全局 freeze 策略请使用 `appearance-panel-open`（由 registry 统一维护）。
  if (typeof document === 'undefined') {
    return
  }

  const value = isOpen ? 'true' : 'false'
  document.documentElement.setAttribute('software-theme-panel-open', value)
  document.body.setAttribute('software-theme-panel-open', value)
}

const applySoftwareThemeModeAttribute = (mode: SoftwareThemeMode) => {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  const body = document.body
  const isThemeActive = mode !== 'system'

  root.setAttribute('software-theme-mode', mode)
  body.setAttribute('software-theme-mode', mode)
  root.setAttribute('software-theme-active', isThemeActive ? 'true' : 'false')
  body.setAttribute('software-theme-active', isThemeActive ? 'true' : 'false')
}

const applySoftwareThemePresetAttribute = (state: SoftwareThemeState) => {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  const body = document.body
  const preset = state.mode === 'preset' ? (state.preset ?? 'system') : state.mode

  root.setAttribute('software-theme-preset', preset)
  body.setAttribute('software-theme-preset', preset)
}

const applySoftwareThemeToneAttribute = (tone: 'light' | 'dark' | 'brand') => {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.setAttribute('software-theme-tone', tone)
  document.body.setAttribute('software-theme-tone', tone)
}

/**
 * 当用户回到 system 模式时，brand 接管的 token 必须从 brand 表里重新写回，
 * 否则会留下上一组 software_theme 的颜色"残影"。
 */
const restoreBrandManagedThemeTokens = () => {
  if (typeof document === 'undefined') {
    return
  }

  const brandTokens = getActiveBrandTokens()
  const targets = getThemeTargets()

  brandManagedThemeTokenKeys.forEach((token) => {
    const value = brandTokens[token]
    if (!value) {
      return
    }

    targets.forEach((target) => {
      target.style.setProperty(token, value)
    })
  })
}

const patchThemeTokens = (nextTokens: ThemeCssTokens | null) => {
  const targets = getThemeTargets()
  const previousTokens = appliedThemeTokens

  if (previousTokens) {
    Object.keys(previousTokens).forEach((token) => {
      if (nextTokens?.[token] !== undefined) {
        return
      }

      targets.forEach((target) => {
        target.style.removeProperty(token)
      })
    })
  }

  if (!nextTokens) {
    appliedThemeTokens = null
    return
  }

  Object.entries(nextTokens).forEach(([token, value]) => {
    if (previousTokens?.[token] === value) {
      return
    }

    targets.forEach((target) => {
      target.style.setProperty(token, value)
    })
  })

  appliedThemeTokens = { ...nextTokens }
}

const applySoftwareTheme = () => {
  if (typeof document === 'undefined') {
    return
  }

  applySoftwareThemeModeAttribute(themeState.mode)
  applySoftwareThemePresetAttribute(themeState)

  const tokens = resolveThemeTokens(themeState)
  applySoftwareThemeToneAttribute(resolveSoftwareThemeTone(tokens))
  patchThemeTokens(tokens)

  if (!tokens) {
    restoreBrandManagedThemeTokens()
  }
}

// ────────────────────────────────────────────────────────────────────────────────
// region: 持久化与调度
// ────────────────────────────────────────────────────────────────────────────────

const persistSoftwareTheme = () => {
  if (!canUseStorage()) {
    return
  }

  const isDefaultState =
    themeState.mode === DEFAULT_THEME.mode &&
    themeState.preset === DEFAULT_THEME.preset &&
    JSON.stringify(themeState.customColors) === JSON.stringify(DEFAULT_THEME.customColors) &&
    JSON.stringify(themeState.customSettings) === JSON.stringify(DEFAULT_THEME.customSettings)

  if (isDefaultState) {
    localStorage.removeItem(STORAGE_KEYS.SOFTWARE_THEME)
    return
  }

  const payload: SoftwareThemeStorage = {
    version: SOFTWARE_THEME_SCHEMA_VERSION,
    mode: themeState.mode,
    preset: themeState.preset,
    customColors: themeState.customColors,
    customSettings: themeState.customSettings,
  }

  localStorage.setItem(STORAGE_KEYS.SOFTWARE_THEME, JSON.stringify(payload))
}

const flushSoftwareThemePersistence = () => {
  if (pendingPersistTimer !== null) {
    clearTimeout(pendingPersistTimer)
    pendingPersistTimer = null
  }

  persistSoftwareTheme()
}

const scheduleSoftwareThemePersistence = () => {
  if (!canUseStorage()) {
    return
  }

  if (pendingPersistTimer !== null) {
    clearTimeout(pendingPersistTimer)
  }

  pendingPersistTimer = window.setTimeout(() => {
    pendingPersistTimer = null
    persistSoftwareTheme()
  }, 120)
}

const flushSoftwareThemeApply = () => {
  if (typeof window === 'undefined') {
    applySoftwareTheme()
    return
  }

  if (pendingApplyTimer !== null) {
    clearTimeout(pendingApplyTimer)
    pendingApplyTimer = null
  }

  if (pendingApplyFrame) {
    window.cancelAnimationFrame(pendingApplyFrame)
    pendingApplyFrame = 0
  }

  applySoftwareTheme()
}

const scheduleSoftwareThemeApply = (debounceMs = 0) => {
  if (typeof window === 'undefined') {
    applySoftwareTheme()
    return
  }

  if (pendingApplyTimer !== null) {
    clearTimeout(pendingApplyTimer)
    pendingApplyTimer = null
  }

  if (debounceMs > 0) {
    if (pendingApplyFrame) {
      window.cancelAnimationFrame(pendingApplyFrame)
      pendingApplyFrame = 0
    }

    pendingApplyTimer = window.setTimeout(() => {
      pendingApplyTimer = null
      applySoftwareTheme()
    }, debounceMs)
    return
  }

  if (pendingApplyFrame) {
    return
  }

  pendingApplyFrame = window.requestAnimationFrame(() => {
    pendingApplyFrame = 0
    applySoftwareTheme()
  })
}

const syncSoftwareTheme = (debounceApplyMs = 0) => {
  scheduleSoftwareThemeApply(debounceApplyMs)
  scheduleSoftwareThemePersistence()
}

// ────────────────────────────────────────────────────────────────────────────────
// region: Public API
// ────────────────────────────────────────────────────────────────────────────────

export const initializeSoftwareTheme = () => {
  if (initialized) {
    return
  }

  initialized = true

  if (canUseStorage()) {
    try {
      const storedValue = localStorage.getItem(STORAGE_KEYS.SOFTWARE_THEME)
      if (storedValue) {
        const parsed = normalizeSoftwareTheme(JSON.parse(storedValue) as unknown)
        if (parsed) {
          Object.assign(themeState, parsed)
        } else {
          localStorage.removeItem(STORAGE_KEYS.SOFTWARE_THEME)
        }
      }
    } catch (_error) {
      localStorage.removeItem(STORAGE_KEYS.SOFTWARE_THEME)
      Object.assign(themeState, {
        ...DEFAULT_THEME,
        customColors: { ...DEFAULT_CUSTOM_COLORS },
        customSettings: { ...DEFAULT_CUSTOM_SETTINGS },
      })
    }
  }

  applySoftwareTheme()
  applySoftwareThemePanelStateAttribute(false)
  registerAppearancePanelCloser(APPEARANCE_PANEL_ID, closeSoftwareThemePanel)
}

/**
 * brand 切换后调用：使所有 computed 重新计算 (依赖 softwareThemeBrandRefreshKey)，
 * 同时强制清空已写入 token 缓存以触发完整 patch。
 */
export const reapplySoftwareTheme = () => {
  softwareThemeBrandRefreshKey.value += 1
  appliedThemeTokens = null
  applySoftwareTheme()
}

export const softwareThemeOptions = computed(() => {
  // 触发依赖：brand 变化时刷新预设色板
  void softwareThemeBrandRefreshKey.value
  return resolveSoftwareThemeOptions()
})

export const setSoftwareThemePreset = (preset: SoftwareThemePreset) => {
  if (themeState.mode === 'preset' && themeState.preset === preset) {
    return
  }

  themeState.mode = 'preset'
  themeState.preset = preset
  syncSoftwareTheme()
}

export const setSoftwareThemeCustomColors = (partialColors: Partial<SoftwareThemeCustomColors>) => {
  themeState.customColors = {
    ...themeState.customColors,
    ...normalizePartialCustomColors(partialColors),
  }
  themeState.mode = 'custom'
  themeState.preset = null
  syncSoftwareTheme(softwareThemePanelVisible.value ? CUSTOM_THEME_APPLY_DEBOUNCE : 0)
}

export const setSoftwareThemeCustomSettings = (partialSettings: Partial<SoftwareThemeCustomSettings>) => {
  themeState.customSettings = {
    ...themeState.customSettings,
    ...normalizePartialCustomSettings(partialSettings),
  }
  themeState.mode = 'custom'
  themeState.preset = null
  syncSoftwareTheme(softwareThemePanelVisible.value ? CUSTOM_THEME_APPLY_DEBOUNCE : 0)
}

export const setSoftwareThemeMode = (mode: SoftwareThemeMode) => {
  if (mode === 'preset' && !themeState.preset) {
    themeState.preset = 'daylight'
  }

  if (mode === 'system' || mode === 'custom') {
    themeState.preset = null
  }

  themeState.mode = mode
  syncSoftwareTheme()
}

export const resetSoftwareTheme = () => {
  Object.assign(themeState, {
    ...DEFAULT_THEME,
    customColors: { ...DEFAULT_CUSTOM_COLORS },
    customSettings: { ...DEFAULT_CUSTOM_SETTINGS },
  })
  syncSoftwareTheme()
}

/**
 * 自定义预设卡片预览三色：[浅色衍生, 舞台主色, 强调色]
 * 结构对齐预设 swatches: [light, stage, accent]
 * 直接使用用户设置的原始色值，确保预览"所见即所得"
 */
export const softwareThemeCustomPreviewSwatches = computed((): [string, string, string] => {
  const stage = normalizeHexColor(themeState.customColors.stage, DEFAULT_CUSTOM_COLORS.stage)
  const accent = normalizeHexColor(themeState.customColors.accent, DEFAULT_CUSTOM_COLORS.accent)
  // 浅色衍生：将 stage 与白色混合 72%，作为卡片内的亮色小键
  const lightSwatch = mixColor(stage, '#ffffff', 0.72)
  return [lightSwatch, stage, accent]
})

export const softwareThemeStudioStyle = computed(() => {
  void softwareThemeBrandRefreshKey.value
  const tokens = resolveThemeTokens(themeState)
  const palette = resolveStudioPalette(tokens)
  const systemPreviewPalette = resolveSystemPreviewPalette()
  const studioStagePalette = resolveStudioStagePalette(tokens)

  return {
    '--theme-stage-bg': studioStagePalette.background,
    '--software-theme-accent': palette.accent,
    '--software-theme-accent-soft': palette.accentSoft,
    '--software-theme-accent-border': palette.accentBorder,
    '--software-theme-surface-soft': palette.surfaceSoft,
    '--software-theme-surface-strong': palette.surfaceStrong,
    '--software-theme-shadow-soft': palette.shadowSoft,
    '--software-theme-shadow-strong': palette.shadowStrong,
    '--software-theme-button-bg': palette.buttonBg,
    '--software-theme-button-shadow': palette.buttonShadow,
    '--software-theme-button-shadow-hover': palette.buttonShadowHover,
    '--software-theme-system-card-bg': systemPreviewPalette.card,
    '--software-theme-system-swatch-soft': systemPreviewPalette.soft,
    '--software-theme-system-swatch-brand': systemPreviewPalette.brand,
    '--software-theme-system-swatch-deep': systemPreviewPalette.deep,
  }
})

export const openSoftwareThemePanel = () => {
  closeOtherAppearancePanels(APPEARANCE_PANEL_ID)
  softwareThemePanelVisible.value = true
  applySoftwareThemePanelStateAttribute(true)
  notifyAppearancePanelOpened(APPEARANCE_PANEL_ID)
}

export const closeSoftwareThemePanel = () => {
  softwareThemePanelVisible.value = false
  applySoftwareThemePanelStateAttribute(false)
  notifyAppearancePanelClosed(APPEARANCE_PANEL_ID)
  // 关闭面板时若仍有挂起的自定义颜色 debounce，立即刷盘，避免用户重开面板看到旧值。
  flushSoftwareThemePersistence()
}

export const softwareTheme = themeState
